import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { readAuthToken } from '@noor/api';
import { env } from '@noor/config';
import {
  buildTransactionsListFilter,
  transactionListSearchSchema,
  transactionQueries,
  type TransactionListSearch,
} from '@/entities/reports/transaction';
import { TransactionsPage } from '@/pages/reports/transactions';

/**
 * Transactions report — cpanel `/reports/transactions`. The URL is the single
 * source of truth for the day window, search, filters and page. Prefetch is
 * best-effort and non-blocking; the table surfaces its own loading state.
 * Gated by the `reports.transactions.view` permission.
 */
export const Route = createFileRoute('/reports/transactions')({
  staticData: { permission: 'reports.transactions.view' },
  beforeLoad: () => {
    // Auth guard: against the real API a token is required. In MSW dev mode the
    // mock backend authenticates everything, so the guard only bites with MSW off.
    if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
      // `redirect()` is TanStack Router's control-flow throw, not an Error.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login' });
    }
  },
  validateSearch: transactionListSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => {
    void context.queryClient
      .prefetchQuery(transactionQueries.list(buildTransactionsListFilter(deps)))
      .catch(() => undefined);
  },
  component: TransactionsRoute,
});

function TransactionsRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onParamsChange = (next: Partial<TransactionListSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...next }) });
  };

  return <TransactionsPage params={search} onParamsChange={onParamsChange} />;
}
